import { useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Users, X } from 'lucide-react'
import { deleteClient, listClients } from '../../api/clients'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox } from '../../components/ui/Card'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass, skeletonClass } from '@/lib/tableStyles'
import type { ClientListItem } from '../../types/clients'

const PAGE_SIZE = 20

export default function ClientList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') ?? ''
  const offset = Number(searchParams.get('offset') ?? '0')

  const [inputValue, setInputValue] = useState(search)
  const [archiving, setArchiving] = useState<number | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toast = useToast()
  const confirm = useConfirm()

  const { data, loading, error, refetch } = useFetch(
    () => listClients({ search: search || undefined, limit: PAGE_SIZE, offset }),
    [search, offset],
  )

  const clients: ClientListItem[] = data?.items ?? []
  const total = data?.total ?? 0

  const handleSearch = (value: string) => {
    setInputValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchParams(value ? { q: value } : {}, { replace: true })
    }, 300)
  }

  const handleClear = () => {
    setInputValue('')
    setSearchParams({}, { replace: true })
  }

  const handleArchive = async (client: ClientListItem) => {
    const ok = await confirm({
      title: 'Archive client?',
      message: `${client.first_name} ${client.last_name} will be archived and won't appear in active lists.`,
      confirmLabel: 'Archive',
    })
    if (!ok) return
    setArchiving(client.id)
    try {
      await deleteClient(client.id)
      toast.success(`${client.first_name} ${client.last_name} has been archived.`)
      refetch()
    } catch {
      toast.error('Failed to archive client. Please try again.')
    } finally {
      setArchiving(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const start = total === 0 ? 0 : offset + 1
  const end = Math.min(offset + PAGE_SIZE, total)

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Clients</h1>
        <ButtonLink to="/clients/new">+ New Client</ButtonLink>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-[360px]">
          <input
            className="w-full py-[0.6rem] pl-[0.85rem] pr-8 border border-border-input rounded text-[0.9rem] outline-none bg-surface text-text box-border"
            type="search"
            placeholder="Search name, email, phone…"
            value={inputValue}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {inputValue && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-none text-text-3 cursor-pointer leading-none p-0 flex items-center"
              onClick={handleClear}
            >
              <X size={14} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Name</th>
                <th className={thClass}>Phone</th>
                <th className={thClass}>Email</th>
                <th className={`${thClass} text-center`}>Patients</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className={tdClass}>
                        <div className={`${skeletonClass} h-3.5 ${j === 3 ? 'w-10' : j === 4 ? 'w-20' : 'w-4/5'}`} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={5} className={tdClass}>
                    <div className="text-center py-12 px-4 text-text-3">
                      <Users size={36} strokeWidth={1.25} className="text-text-4 mb-2 mx-auto" />
                      <div className="font-semibold text-text-heading mb-[0.35rem]">No clients found</div>
                      {search ? (
                        <div className="text-sm">Try a different search term</div>
                      ) : (
                        <>
                          <div className="text-sm mb-4">Add your first client to get started</div>
                          <ButtonLink to="/clients/new">+ New Client</ButtonLink>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                clients.map((c) => (
                  <tr key={c.id}>
                    <td className={tdClass}>
                      <Link to={`/clients/${c.id}`} className="text-accent no-underline font-semibold hover:underline">
                        {c.last_name}, {c.first_name}
                      </Link>
                    </td>
                    <td className={`${tdClass} text-text-2`}>{c.phone}</td>
                    <td className={`${tdClass} text-text-2`}>{c.email}</td>
                    <td className={`${tdClass} text-center`}>
                      <span className="inline-block bg-surface-alt text-accent rounded-full py-[0.15rem] px-[0.65rem] text-[0.8rem] font-semibold">
                        {c.patient_count}
                      </span>
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <Link to={`/clients/${c.id}/edit`} className="text-accent font-medium text-[0.82rem] no-underline">
                        Edit
                      </Link>
                      {' '}
                      <button
                        className="bg-transparent border-none cursor-pointer text-[0.82rem] font-medium py-[0.25rem] px-[0.4rem] rounded text-danger-text"
                        onClick={() => void handleArchive(c)}
                        disabled={archiving === c.id}
                      >
                        {archiving === c.id ? '…' : 'Archive'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-[0.85rem] border-t border-border text-[0.85rem] text-text-2">
            <span>Showing {start}–{end} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setSearchParams(search ? { q: search, offset: String(Math.max(0, offset - PAGE_SIZE)) } : { offset: String(Math.max(0, offset - PAGE_SIZE)) }, { replace: true })}>
                ← Prev
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setSearchParams(search ? { q: search, offset: String(offset + PAGE_SIZE) } : { offset: String(offset + PAGE_SIZE) }, { replace: true })}>
                Next →
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
